//SignIn_Auth0.ts =================================================
// Website: https://auth0.com/
// Code Examples: https://auth0.com/docs/libraries/auth0-single-page-app-sdk#create-the-client
// 1) a) Create a new "Single Page Web Application" call it "GIS Website" or whatever

//    b) Set Settings -> "Allowed Callback URLs" to:
//       https:// Your WEBSERVER URL/, https:// Your WEBSERVER URL/YourWebsiteNameTS/, http://localhost:1337/ (for VS)
//       Note - The trailing slash should be entered and its case sensitive.

//    c) Set "Allowed Logout URLs" to https:// Your WEBSERVER URL/YourWebsiteNameTS/, http://localhost:1337/ (for VS)
//       Note - The trailing slash is required and its case sensitive.

//    d) Set "Allowed Web Origins" to https://Your WEBSERVER URL/, http://localhost:1337/
//       Note - The trailing slash is required and its case sensitive.

//    e) Refresh Token Rotation - Enable it.  Also verify under Advanced Settings -> Grant Types that Refresh Token is checked on.

//    f) Save Changes

// 2) Authentication->Database->Username-Password-Authentication->Disable sign ups should be activated

// 3) Branding->Universal login->Settings: Upload logo and set colors if desired. Save changes.
//    Settings->General: To set the friendly name of your domain.

// 4) Applications->Applications->GIS Website->Settings->Copy "Domain" and "Client ID" into Settings.ts

// 5) a) User Management->Users->Create User->Enter Email and Password
//    b) Edit the "Name" to be more readable and to set the security access level. Example: 7 ! Joe Bloe
//    c) Repeat for each new user
//    d) Have each user verify their new Auth0 login in their email.

import * as Settings from "../Settings";
import * as Util from "./Util";

import { createAuth0Client, Auth0Client } from '@auth0/auth0-spa-js';

export let signedIn: boolean = false;
export let email: string = "";
export let userName: string = "";
export let accessLevel: number = 0;

let auth0_client: Auth0Client;

//Declare the callback function.
interface iCallbacks {
   success(): void;
   fail(): void;
}

export async function authenticate(callbacks: iCallbacks) {
   Util.logMsg("Auth0: Creating API.");
   Util.logMsg("redirectUri:", window.location.href);
   const callback = callbacks;

   try {
      auth0_client = await createAuth0Client({
         domain: Settings.auth0_domain,
         clientId: Settings.auth0_clientId,
         useRefreshTokens: true, //Required to bypass 3rd party cookie blocking.
         cacheLocation: "localstorage", //Required so user does not have to click the sign-in button if signed in previously.
         //authorizationParams: { audience: "https://" + Settings.auth0_domain + "/api/v2/", scope: "openid profile email offline_access" },
      });
      Util.logMsg("Created API - auth0_client", auth0_client);

      //Handle code for Safari on IOS devices. !CUSTOM Poway - Commented out. Not using popup for desktop so mobile and desktop are the same.
      //if (isMobileiOS()) {
      //   const query = window.location.search;
      //   if (query.includes("code=") && query.includes("state=")) {
      //      Util.logMsg("Calling handleRedirectCallback for Safari IOS");
      //      await auth0_client.handleRedirectCallback();
      //      window.history.replaceState({}, document.title, window.location.href); // clean up URL
      //   }
      //}

      const query = window.location.search;
      if (query.includes("code=") && query.includes("state=")) {
         Util.logMsg("Calling handleRedirectCallback for Safari IOS");
         await auth0_client.handleRedirectCallback();
         window.history.replaceState({}, document.title, window.location.href); // clean up URL
      }

      const authenticated = await auth0_client.isAuthenticated();
      Util.logMsg("Authenticated true/false", authenticated);

      if (authenticated) {
         Util.logMsg("Yeppers, it is authenticated");
         const user = await auth0_client.getUser();

         Util.logMsg("Login: Getting Auth0 user information: ", user);

         if (user) {
            signedIn = true;
            email = user.email;
            userName = user.nickname; // Prefix portion of the users email address.
            accessLevel = Util.parseInteger(user.given_name); //Access level comes from the edited "Name" in Auth0.  Example: 9 ! Joe Bloe !CUSTOM Poway - Changed from user.name to user.given_name
            if (accessLevel === 0) {
               accessLevel = 1; //Default for at least having an auth0 account.
            }
            callback.success();
         } else {
            callback.fail();
         }

      } else {
         Util.logMsg("Nope, Not authenticated");
         callback.fail();
      }

   } catch (error: unknown) {
      Util.errMsg("createAuth0Client or isAuthenticated failed:", error);
      callback.fail();
   }
}

//Sign in.
export async function signIn() {
   try {
      Util.logMsg("Auth0: Logging in with Redirect.");
      if (isMobileiOS()) {
         await auth0_client.loginWithRedirect({ authorizationParams: { redirect_uri: window.location.href } });

      } else {
         //await auth0_client.loginWithPopup({}); !CUSTOM Poway - Commented out. Popup was not working.
         await auth0_client.loginWithRedirect({ authorizationParams: { redirect_uri: window.location.href } });
         Util.logMsg("signIn: Successfully logged in");
      }

   } catch (error: unknown) {
      Util.logMsg("Gracefully closing the login popup");
   }
}

//Sign out.
export function signOut() {
   Util.logMsg("Auth0: logging out.");
   auth0_client.logout({
      //logoutParams: { returnTo: window.location.href } // return URL parameter is required. !CUSTOM Poway - Commented out. Using Util.getPageURL instead.
      logoutParams: { returnTo: Util.getPageURL() } // return URL parameter is required.
   });
}

function isMobileiOS(): boolean {
   const ua = navigator.userAgent;
   const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
   const isTouchScreen = navigator.maxTouchPoints > 1 && /Macintosh/.test(ua);
   return isIOSDevice || isTouchScreen;
}
